import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { eventsApi } from "@/lib/humhub-api/events";
import type { RecurrenceScope } from "@/lib/humhub-api/types";
import { queryInvalidationHelpers } from "@/lib/queryInvalidationHelpers";

interface UseRemoveEventFileReturn {
  isLoading: boolean;
  error: Error | null;
  removeFile: (
    eventId: number,
    fileId: number,
    scope?: RecurrenceScope,
  ) => Promise<void>;
  reset: () => void;
  isSuccess: boolean;
}

export const useRemoveEventFile = (): UseRemoveEventFileReturn => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      eventId,
      fileId,
      scope,
    }: {
      eventId: number;
      fileId: number;
      scope?: RecurrenceScope;
    }) => eventsApi.removeEventFile(eventId, fileId, scope ?? "single"),
    onSuccess: (_, variables) => {
      queryInvalidationHelpers.invalidateEventQueries(queryClient);
      if (variables.scope && variables.scope !== "single") {
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === "eventDetail",
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ["eventDetail", variables.eventId],
        });
      }
    },
    onError: (error) => {
      console.error("Error removing event file:", error);
    },
  });

  const removeFile = useCallback(
    async (
      eventId: number,
      fileId: number,
      scope?: RecurrenceScope,
    ): Promise<void> => mutation.mutateAsync({ eventId, fileId, scope }),
    [mutation],
  );

  const reset = useCallback(() => {
    mutation.reset();
  }, [mutation]);

  return {
    isLoading: mutation.isPending,
    error: mutation.error,
    removeFile,
    reset,
    isSuccess: mutation.isSuccess,
  };
};
